import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { QuoteSuccess } from '@/components/quote/QuoteSuccess';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL_FORM = {
  first_name: '', last_name: '', email: '', phone: '',
  organization: '', designation: '', origin: '', destination: '',
  pickup_date: '', dropoff_date: '', equipment_type: '', message: ''
};

const equipmentTypes = ['Dry Van', 'Flatbed', 'Reefer', 'Power Only', 'Container', 'Step Deck'];

const QuotePage = () => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/quotes`, formData);
      setSubmitted(true);
      toast.success('Quote request submitted successfully!');
    } catch (error) {
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (submitted) {
    return (
      <QuoteSuccess onReset={() => { setSubmitted(false); setFormData(INITIAL_FORM); }} />
    );
  }

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-zinc-900 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-red-500 text-xs font-bold uppercase tracking-[0.2em]">Free Estimate</span>
          <h1 className="text-4xl sm:text-5xl font-black text-white mt-3 mb-6">
            Get a Freight Quote
          </h1>
          <p className="text-zinc-400 max-w-2xl mx-auto text-lg">
            Fill out the form below and our team will provide you with a competitive quote tailored to your shipping needs.
          </p>
        </div>
      </section>

      {/* Quote Form */}
      <section className="py-16 bg-zinc-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-8 lg:p-12 border border-zinc-200 shadow-sm">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Contact Information */}
              <div>
                <h2 className="text-lg font-bold text-zinc-900 mb-4 pb-2 border-b border-zinc-200">
                  Contact Information
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">First Name *</label>
                    <input type="text" name="first_name" value={formData.first_name} onChange={handleChange} required className="w-full px-4 py-3 border border-zinc-300 focus:border-zinc-900 outline-none" placeholder="John" data-testid="quote-first-name" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Last Name *</label>
                    <input type="text" name="last_name" value={formData.last_name} onChange={handleChange} required className="w-full px-4 py-3 border border-zinc-300 focus:border-zinc-900 outline-none" placeholder="Doe" data-testid="quote-last-name" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Email *</label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-3 border border-zinc-300 focus:border-zinc-900 outline-none" placeholder="john@example.com" data-testid="quote-email" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Phone *</label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required className="w-full px-4 py-3 border border-zinc-300 focus:border-zinc-900 outline-none" placeholder="(555) 123-4567" data-testid="quote-phone" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Organization</label>
                    <input type="text" name="organization" value={formData.organization} onChange={handleChange} className="w-full px-4 py-3 border border-zinc-300 focus:border-zinc-900 outline-none" placeholder="Company Name" data-testid="quote-organization" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Designation</label>
                    <input type="text" name="designation" value={formData.designation} onChange={handleChange} className="w-full px-4 py-3 border border-zinc-300 focus:border-zinc-900 outline-none" placeholder="Your Role" data-testid="quote-designation" />
                  </div>
                </div>
              </div>

              {/* Shipment Details */}
              <div>
                <h2 className="text-lg font-bold text-zinc-900 mb-4 pb-2 border-b border-zinc-200">
                  Shipment Details
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Origin *</label>
                    <input type="text" name="origin" value={formData.origin} onChange={handleChange} required className="w-full px-4 py-3 border border-zinc-300 focus:border-zinc-900 outline-none" placeholder="City, State" data-testid="quote-origin" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Destination *</label>
                    <input type="text" name="destination" value={formData.destination} onChange={handleChange} required className="w-full px-4 py-3 border border-zinc-300 focus:border-zinc-900 outline-none" placeholder="City, State" data-testid="quote-destination" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Pickup Date *</label>
                    <input type="date" name="pickup_date" value={formData.pickup_date} onChange={handleChange} required className="w-full px-4 py-3 border border-zinc-300 focus:border-zinc-900 outline-none" data-testid="quote-pickup-date" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Delivery Date *</label>
                    <input type="date" name="dropoff_date" value={formData.dropoff_date} onChange={handleChange} required className="w-full px-4 py-3 border border-zinc-300 focus:border-zinc-900 outline-none" data-testid="quote-dropoff-date" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Equipment Type *</label>
                    <Select
                      value={formData.equipment_type}
                      onValueChange={(value) => setFormData({ ...formData, equipment_type: value })}
                    >
                      <SelectTrigger className="h-12 border-zinc-300" data-testid="quote-equipment">
                        <SelectValue placeholder="Select equipment type" />
                      </SelectTrigger>
                      <SelectContent>
                        {equipmentTypes.map((type) => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Additional Info */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Additional Information</label>
                <textarea name="message" value={formData.message} onChange={handleChange} rows={4} className="w-full px-4 py-3 border border-zinc-300 focus:border-zinc-900 outline-none resize-none" placeholder="Commodity details, special requirements, etc." data-testid="quote-message" />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-4 font-bold uppercase tracking-wide transition-colors disabled:opacity-50 w-full"
                data-testid="quote-submit"
              >
                {submitting ? 'Submitting...' : (
                  <>
                    Get My Quote
                    <Send className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default QuotePage;
